/**
 * useLazyResolution — Risoluzione async di un singolo entity by-id, con
 * cleanup race-safe.
 *
 * Pattern ricorrente: un componente riceve un FK numerico (es.
 * `task.queueId`, `task.assignedUserId`) e ha bisogno del nome leggibile
 * per il display. Il fetch avviene on-demand (quando l'id cambia) e
 * deve essere robusto al cambio rapido (utente che switcha task) per
 * non far apparire dati stale del task precedente.
 *
 * Restituisce `null` quando l'id e' null/0/undefined, o quando il fetch
 * fallisce. La funzione di transform `pick` mappa la response al valore
 * tenuto nello state (es. `(q) => q.name`).
 *
 * Per fetch di N entity by-id (autocomplete options) usare invece
 * `useAsyncAutocomplete` o `useServerPagedSearch`.
 *
 * Esempio:
 * ```tsx
 * const queueName = useLazyResolution(
 *   task.queueId,
 *   (id) => queueApi.getById(id),
 *   (q) => q.name,
 * );
 * ```
 */

import { useEffect, useState } from 'react';

export function useLazyResolution<T, R>(
  id: number | null | undefined,
  fetcher: (id: number) => Promise<T>,
  pick: (item: T) => R,
): R | null {
  const [value, setValue] = useState<R | null>(null);

  useEffect(() => {
    if (!id) {
      setValue(null);
      return;
    }
    let cancelled = false;
    fetcher(id)
      .then((item) => {
        if (!cancelled) setValue(pick(item));
      })
      .catch(() => {
        if (!cancelled) setValue(null);
      });
    return () => {
      cancelled = true;
    };
    // fetcher/pick intenzionalmente esclusi dalle deps: tipicamente
    // chiusure inline ad ogni render, ri-trigger inutile. Il chiamante
    // garantisce che id sia l'unica dipendenza che importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return value;
}
